/**
 * Similar Homes Available matching (NHS20-8827).
 *
 * Ranks candidate homes (redux/selectors.js#selectSimilarHomesCandidates — Available floor
 * plans, Quick Move-Ins, and Model homes; never Sold, Coming Soon, or Unavailable) against one
 * sold home (redux/selectors.js#selectSoldHomes — this runs once per card in the Sold listing
 * sequence, not just the selected one), and returns up to `maxResults`. Pure function — no Redux,
 * no DOM.
 *
 * Matching priority (ticket order):
 *  1. Same floor plan as the sold home, via `planId`. The sold home is always a house plan
 *     (confirmed with product), so this is real, present-today data on that side. Candidate
 *     housing units (QMI/Model) don't carry a planId at all — the API has no such field — so
 *     this tier currently only matches against other floorPlan-kind candidates.
 *  2. Bedrooms within ±1, bathrooms within ±1, square footage within ±25%.
 *
 * @module nativeInteractiveSiteMap/services/similarHomesService
 */

const BEDROOM_TOLERANCE = 1;
const BATHROOM_TOLERANCE = 1;
const SQUARE_FEET_TOLERANCE_RATIO = 0.25;

function isSameFloorPlan(soldHome, candidate) {
  return soldHome.planId != null && candidate.planId != null && soldHome.planId === candidate.planId;
}

function isWithinTolerance(candidateValue, soldValue, tolerance) {
  return candidateValue != null && soldValue != null && Math.abs(candidateValue - soldValue) <= tolerance;
}

function isCloseSpecMatch(soldHome, candidate) {
  if (soldHome.squareFeet == null || candidate.squareFeet == null) {
    return false;
  }
  const squareFeetTolerance = soldHome.squareFeet * SQUARE_FEET_TOLERANCE_RATIO;
  return (
    isWithinTolerance(candidate.bedrooms, soldHome.bedrooms, BEDROOM_TOLERANCE) &&
    isWithinTolerance(candidate.bathrooms, soldHome.bathrooms, BATHROOM_TOLERANCE) &&
    Math.abs(candidate.squareFeet - soldHome.squareFeet) <= squareFeetTolerance
  );
}

/**
 * @param {object|null} soldHome - One sold home (redux/selectors.js#selectSoldHomes entry),
 *   shaped like a house plan: { planId, bedrooms, bathrooms, squareFeet }.
 * @param {object[]} candidates - redux/selectors.js#selectSimilarHomesCandidates output.
 * @param {number} [maxResults=2] - Max homes to return (NHS20-8827: "Display a maximum of 2").
 * @returns {object[]} Up to maxResults candidates — same-floor-plan matches first, then close
 *   spec matches. Empty when soldHome is null or nothing matches.
 */
export default function findSimilarHomes(soldHome, candidates, maxResults = 2) {
  if (!soldHome) {
    return [];
  }

  const samePlanMatches = candidates.filter((candidate) => isSameFloorPlan(soldHome, candidate));
  const closeSpecMatches = candidates.filter(
    (candidate) => !isSameFloorPlan(soldHome, candidate) && isCloseSpecMatch(soldHome, candidate)
  );

  return [...samePlanMatches, ...closeSpecMatches].slice(0, maxResults);
}
